/**
 * Diff a nivel de palabra entre el texto original y el sugerido.
 *
 * Subsecuencia comun mas larga, ~60 lineas. No hace falta una libreria: los
 * textos son parrafos de una hoja de vida, no archivos, y una dependencia mas
 * en el bundle por esto no se justifica.
 *
 * Por palabras y no por caracteres a proposito: un diff por caracteres sobre
 * texto reescrito produce confeti ilegible -- media palabra marcada aqui, dos
 * letras alla. Por palabras se lee que cambio de verdad.
 */

export type TipoTrozo = "igual" | "quitado" | "puesto";

export interface Trozo {
  tipo: TipoTrozo;
  texto: string;
}

/**
 * Separa en palabras CONSERVANDO los espacios y saltos de linea.
 *
 * Los separadores viajan pegados a su palabra para poder reconstruir el texto
 * exacto al pintarlo: sin eso, un diff de un texto con viñetas pierde los
 * saltos y todo acaba en un parrafo.
 */
const enPalabras = (texto: string): string[] =>
  texto.match(/\S+\s*/g) ?? [];

/** Matriz de longitudes de la subsecuencia comun mas larga. */
const matrizLcs = (a: string[], b: string[]): number[][] => {
  const m = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      const fila = m[i];
      const siguiente = m[i + 1];
      if (!fila || !siguiente) continue;
      fila[j] =
        a[i]?.trim() === b[j]?.trim()
          ? (siguiente[j + 1] ?? 0) + 1
          : Math.max(siguiente[j] ?? 0, fila[j + 1] ?? 0);
    }
  }

  return m;
};

export const diffPorPalabra = (original: string, sugerido: string): Trozo[] => {
  const a = enPalabras(original);
  const b = enPalabras(sugerido);

  if (a.length === 0) return b.length ? [{ tipo: "puesto", texto: sugerido }] : [];
  if (b.length === 0) return [{ tipo: "quitado", texto: original }];

  const m = matrizLcs(a, b);
  const trozos: Trozo[] = [];

  // Se acumulan las palabras consecutivas del mismo tipo en un solo trozo, para
  // que el subrayado salga continuo y no una palabra suelta tras otra.
  const empujar = (tipo: TipoTrozo, texto: string) => {
    const ultimo = trozos[trozos.length - 1];
    if (ultimo?.tipo === tipo) ultimo.texto += texto;
    else trozos.push({ tipo, texto });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i]?.trim() === b[j]?.trim()) {
      empujar("igual", b[j] ?? "");
      i++;
      j++;
    } else if ((m[i + 1]?.[j] ?? 0) >= (m[i]?.[j + 1] ?? 0)) {
      empujar("quitado", a[i] ?? "");
      i++;
    } else {
      empujar("puesto", b[j] ?? "");
      j++;
    }
  }

  while (i < a.length) empujar("quitado", a[i++] ?? "");
  while (j < b.length) empujar("puesto", b[j++] ?? "");

  return trozos;
};

/** Cuántas palabras cambian de verdad. Sirve para decir "3 cambios" sin abrir. */
export const contarCambios = (trozos: Trozo[]): number =>
  trozos.filter((t) => t.tipo !== "igual").length;
